import React, { useEffect, useState } from "react";
import { X, HelpCircle, Info, ExternalLink } from "lucide-react";

interface HelpAboutModalProps {
  onClose: () => void;
}

type Tab = "help" | "about";

const SHORTCUTS: Array<[string, string]> = [
  ["Ctrl + Scroll", "Zoom in / out"],
  ["Ctrl + F", "Find in document"],
  ["Ctrl + =  /  Ctrl + -", "Zoom in / out"],
  ["Ctrl + 0", "Fit to width"],
  ["Ctrl + P", "Print"],
];

/** Modal with a Help tab (quick guide) and an About tab (version, links). */
const HelpAboutModal: React.FC<HelpAboutModalProps> = ({ onClose }) => {
  const [tab, setTab] = useState<Tab>("help");
  const [version, setVersion] = useState<string>("");

  useEffect(() => {
    window.electronAPI?.getAppVersion().then(setVersion).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const makeDefault = () => {
    window.electronAPI?.openDefaultAppsSettings();
  };

  return (
    <div className="help-modal-overlay" onClick={onClose}>
      <div className="help-modal" onClick={(e) => e.stopPropagation()}>
        <div className="help-modal-header">
          <div className="help-modal-tabs">
            <button
              className={`help-modal-tab ${tab === "help" ? "help-modal-tab-active" : ""}`}
              onClick={() => setTab("help")}
            >
              <HelpCircle size={14} /> Help
            </button>
            <button
              className={`help-modal-tab ${tab === "about" ? "help-modal-tab-active" : ""}`}
              onClick={() => setTab("about")}
            >
              <Info size={14} /> About
            </button>
          </div>
          <button className="help-modal-close" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {tab === "help" ? (
          <div className="help-modal-body">
            <h3>Getting around</h3>
            <p>
              Open a PDF from the "Open" button, drag a file onto the window, or pick a
              recent file from the start screen. The left rail shows page thumbnails; the
              right rail holds the editing tools for the open document.
            </p>
            <h3>Shortcuts</h3>
            <table className="help-shortcut-table">
              <tbody>
                {SHORTCUTS.map(([key, desc]) => (
                  <tr key={key}>
                    <td className="help-shortcut-key">{key}</td>
                    <td>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h3>Make HyperPDF your default PDF viewer</h3>
            <p>
              Windows asks you to confirm which app opens PDFs by default. Click below to
              open the system settings, then choose HyperPDF for the .pdf file type.
            </p>
            <button className="tool-action" onClick={makeDefault}>
              <ExternalLink size={14} /> Open default apps settings
            </button>
          </div>
        ) : (
          <div className="help-modal-body">
            <h3>HyperPDF</h3>
            <p>Version {version || "—"}</p>
            <p>A fast, document-first desktop PDF viewer and editor.</p>
            <p>
              © 2025 HyperDIM Innovation ·{" "}
              <a
                href="https://www.hyperdim-innovation.com/"
                target="_blank"
                rel="noreferrer"
              >
                hyperdim-innovation.com
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpAboutModal;
